import { useCallback, useEffect, useState } from "react";
import { AlarmClock, Gavel, Loader2, Lock, Plus, ShieldAlert, Timer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, shortDate } from "@/lib/format";

const BLANK = {
  subject_name: "", subject_email: "", subject_type: "donor", request_type: "access",
  jurisdiction: "IN", received_on: new Date().toISOString().slice(0, 10), detail: "",
  linked_record_id: "",
};

const SLA_STYLE = {
  on_track: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  at_risk: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  breached: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  on_hold: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  closed: "border-border bg-muted text-muted-foreground",
  unknown: "border-border bg-muted text-muted-foreground",
};

const STATUS_STYLE = {
  received: "border-primary/40 bg-primary/10 text-primary",
  verifying: "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300",
  in_progress: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  fulfilled: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  refused: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  on_hold_legal: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
};

const NEXT_STATUS = ["verifying", "in_progress", "fulfilled", "refused"];

export default function Privacy() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [detail, setDetail] = useState(null);
  const [holdReason, setHoldReason] = useState("Statutory retention under audit");

  const load = useCallback(() => api.get("/privacy/requests").then((r) => setData(r.data)), []);
  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const { data: created } = await api.post("/privacy/requests", form);
      toast.success(`${created.request_id} logged — due ${shortDate(created.due_on)} (${created.statutory_days} days)`);
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const setStatus = async (item, status) => {
    setBusy(item.id);
    try {
      const { data: updated } = await api.post(`/privacy/requests/${item.id}/status`, { status, note: "" });
      toast.success(`${item.request_id} → ${status.replace(/_/g, " ")}`);
      if (detail?.id === item.id) setDetail(updated);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const toggleHold = async (item, on) => {
    setBusy(item.id);
    try {
      const { data: updated } = await api.post(`/privacy/requests/${item.id}/legal-hold`, {
        legal_hold: on, reason: on ? holdReason : "",
      });
      toast.success(on ? "Legal hold applied — erasure suspended" : "Legal hold released");
      if (detail?.id === item.id) setDetail(updated);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (data === null) return <Loading label="Loading rights requests" />;
  if (data === false) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="SCR-021 · Privacy Rights Requests" title="Access restricted"
                    description="Your role does not include access to the privacy rights register." />
        <Panel testId="privacy-forbidden">
          <div className="p-8 text-center text-xs text-muted-foreground">
            Rights requests are visible to Compliance, Legal, Org Admin and Auditor roles only.
          </div>
        </Panel>
      </div>
    );
  }

  const c = data.counters;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-021 · Privacy Rights Requests (DSAR)"
        title="Every rights request, on the statutory clock"
        description="Access, correction, erasure, portability and objection requests with a jurisdiction-derived deadline. A legal hold suspends erasure and is recorded with its reason (FR-RISK-005 / FR-RISK-006)."
        actions={can("privacy", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-request-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> Log request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader>
                <DialogTitle>Log a rights request</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  The statutory deadline is derived from the jurisdiction: 30 days for IN/GB/EU/SG/KE/AU, 45 days for US.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Data subject" full>
                  <Input value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })}
                         data-testid="request-subject-input" className="bg-muted/40" />
                </F>
                <F label="Email">
                  <Input value={form.subject_email} onChange={(e) => setForm({ ...form, subject_email: e.target.value })}
                         data-testid="request-email-input" className="bg-muted/40 text-xs" />
                </F>
                <F label="Subject type">
                  <Select value={form.subject_type} onValueChange={(v) => setForm({ ...form, subject_type: v })}>
                    <SelectTrigger data-testid="request-subjecttype-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["donor", "beneficiary", "staff", "volunteer", "trustee", "other"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Request type">
                  <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                    <SelectTrigger data-testid="request-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["access", "correction", "erasure", "portability", "objection"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Jurisdiction">
                  <Select value={form.jurisdiction} onValueChange={(v) => setForm({ ...form, jurisdiction: v })}>
                    <SelectTrigger data-testid="request-jurisdiction-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["IN", "GB", "EU", "US", "SG", "KE", "AU"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Received on">
                  <Input type="date" value={form.received_on}
                         onChange={(e) => setForm({ ...form, received_on: e.target.value })}
                         data-testid="request-received-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Linked record">
                  <Input value={form.linked_record_id} onChange={(e) => setForm({ ...form, linked_record_id: e.target.value })}
                         data-testid="request-linked-input" className="bg-muted/40 font-mono text-xs" />
                </F>
                <F label="Request detail" full>
                  <Textarea rows={2} value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })}
                            data-testid="request-detail-input" className="bg-muted/40 text-xs" />
                </F>
              </div>
              <Button onClick={save} disabled={busy === "save" || !form.subject_name} data-testid="request-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start the clock
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="stagger grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi testId="kpi-requests-open" label="Open requests" value={c.open} accent="sky" icon={Timer}
             sub={`${c.total} logged in total`} />
        <Kpi testId="kpi-requests-atrisk" label="Due within 7 days" value={c.at_risk} accent="amber" icon={AlarmClock} />
        <Kpi testId="kpi-requests-breached" label="Deadline breached" value={c.breached} accent="red" icon={ShieldAlert} />
        <Kpi testId="kpi-requests-hold" label="Under legal hold" value={c.legal_hold} accent="violet" icon={Lock} />
        <Kpi testId="kpi-requests-fulfilled" label="Fulfilled" value={c.fulfilled} accent="emerald" />
      </div>

      <Panel testId="panel-requests" kicker="Statutory clock" title="Rights requests register">
        <Table testId="requests-table"
               head={<><Th>Request</Th><Th>Subject</Th><Th>Type</Th><Th>Jurisdiction</Th><Th>Received</Th><Th>Due</Th><Th>Countdown</Th><Th>Status</Th><Th /></>}>
          {data.items.length === 0 && <Empty colSpan={9} label="No rights requests logged" />}
          {data.items.map((r) => (
            <tr key={r.id} className="row-hover cursor-pointer" data-testid={`request-row-${r.request_id}`}
                onClick={() => setDetail(r)}>
              <Td>
                <div className="font-mono text-[11px] text-primary">{r.request_id}</div>
                {r.legal_hold && (
                  <Chip testId={`hold-chip-${r.request_id}`} className="mt-1 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                    <Lock className="h-3 w-3" /> legal hold
                  </Chip>
                )}
              </Td>
              <Td>
                <div className="text-foreground">{r.subject_name}</div>
                <div className="font-mono text-[10px] text-muted-foreground/70">{r.subject_type}</div>
              </Td>
              <Td><Chip className="border-border bg-muted text-foreground/80">{r.request_type}</Chip></Td>
              <Td className="font-mono text-xs text-muted-foreground">{r.jurisdiction}</Td>
              <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(r.received_on)}</Td>
              <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(r.due_on)}</Td>
              <Td>
                <Chip className={SLA_STYLE[r.sla_state]} testId={`sla-${r.request_id}`}>
                  {r.sla_state === "closed" ? "closed"
                    : r.days_remaining < 0 ? `${Math.abs(r.days_remaining)}d over`
                    : `${r.days_remaining}d left`}
                </Chip>
              </Td>
              <Td>
                <Chip className={STATUS_STYLE[r.status]} testId={`status-${r.request_id}`}>
                  {r.status.replace(/_/g, " ")}
                </Chip>
              </Td>
              <Td onClick={(e) => e.stopPropagation()}>
                {can("privacy", "write") && !["fulfilled", "refused"].includes(r.status) && (
                  <Button size="sm" variant="outline" disabled={busy === r.id}
                          onClick={() => setStatus(r, r.request_type === "erasure" && r.legal_hold ? "in_progress" : "fulfilled")}
                          data-testid={`advance-${r.request_id}`}
                          className="h-7 rounded-full border-border px-2.5 text-[10px]">
                    {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" />
                      : r.request_type === "erasure" && r.legal_hold ? "Attempt fulfil" : "Fulfil"}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Sheet open={Boolean(detail)} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-card sm:max-w-lg">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-foreground" data-testid="request-detail-id">{detail.request_id}</SheetTitle>
              </SheetHeader>

              <div className="mt-4 flex flex-wrap gap-1.5">
                <Chip className={STATUS_STYLE[detail.status]}>{detail.status.replace(/_/g, " ")}</Chip>
                <Chip className={SLA_STYLE[detail.sla_state]}>
                  {detail.days_remaining < 0 ? `${Math.abs(detail.days_remaining)}d over` : `${detail.days_remaining}d left`}
                </Chip>
                <Chip className="border-border bg-muted text-foreground/80">{detail.request_type}</Chip>
              </div>

              <div className="mt-4 space-y-1 rounded border border-border p-3 text-xs">
                <Line k="Data subject" v={detail.subject_name} />
                <Line k="Email" v={detail.subject_email || "—"} />
                <Line k="Subject type" v={detail.subject_type} />
                <Line k="Jurisdiction" v={`${detail.jurisdiction} · ${detail.statutory_days} day SLA`} />
                <Line k="Received" v={shortDate(detail.received_on)} />
                <Line k="Due" v={shortDate(detail.due_on)} />
                <Line k="Linked record" v={detail.linked_record_id || "—"} />
              </div>

              {detail.detail && <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{detail.detail}</p>}

              {detail.legal_hold && (
                <div className="mt-4 flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300"
                     data-testid="legal-hold-banner">
                  <Gavel className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Legal hold in force — erasure cannot be fulfilled. {detail.legal_hold_reason}</span>
                </div>
              )}

              {can("privacy", "write") && (
                <div className="mt-4">
                  <div className="kicker mb-2">Advance status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {NEXT_STATUS.filter((s) => s !== detail.status).map((s) => (
                      <Button key={s} size="sm" variant="outline" disabled={busy === detail.id}
                              onClick={() => setStatus(detail, s)} data-testid={`detail-status-${s}`}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        {s.replace(/_/g, " ")}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {can("privacy", "legal_hold") && (
                <div className="mt-4 space-y-2 rounded border border-border p-3">
                  <div className="kicker">Retention & legal hold</div>
                  {!detail.legal_hold && (
                    <Input value={holdReason} onChange={(e) => setHoldReason(e.target.value)}
                           data-testid="hold-reason-input" className="h-8 bg-muted/40 text-xs" />
                  )}
                  <Button size="sm" onClick={() => toggleHold(detail, !detail.legal_hold)} disabled={busy === detail.id}
                          data-testid="toggle-legal-hold-button"
                          className={`rounded-full text-xs ${detail.legal_hold
                            ? "bg-muted hover:bg-muted" : "bg-amber-500/90 text-white hover:bg-amber-400"}`}>
                    {busy === detail.id ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Lock className="mr-1.5 h-3 w-3" />}
                    {detail.legal_hold ? "Release legal hold" : "Apply legal hold"}
                  </Button>
                </div>
              )}

              <div className="mt-5">
                <div className="kicker mb-2">Action trail</div>
                <div className="space-y-1">
                  {(detail.actions || []).map((a, i) => (
                    <div key={i} className="rounded bg-muted/40 px-2 py-1.5 text-[11px]">
                      <div className="text-foreground/80">{a.action.replace(/_/g, " ")}{a.note ? ` — ${a.note}` : ""}</div>
                      <div className="font-mono text-[10px] text-muted-foreground/70">{a.by} · {dateTime(a.at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Line = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{k}</span>
    <span className="text-right text-foreground/80">{String(v)}</span>
  </div>
);
